/**
 * FamilySharingPanel · Milestones 1 + 2
 *
 * Renders the Cruise Home Sharing surface. Two behaviours in one panel:
 *
 *   1. FTUE (Milestone 1) — shown when the caller has never shared any
 *      cruise AND has not recently dismissed the panel. Warm invitation
 *      to try sharing, with primary CTA "Invite family".
 *   2. Roster + Pending Invitations (Milestone 2) — shown once the
 *      caller has any share record or pending invitation on THIS cruise.
 *      Displays each active Shared Family Member and each pending
 *      invitation row, with a "Share with someone else" affordance to
 *      open the ShareThisCruiseSheet.
 *
 * Consumes:
 *   · DES-FAMILY-SHARING-EXPERIENCE v1.1 §7 (Sharing panel · desktop),
 *     §11 (FTUE), §12 (Share This Cruise Flow), §13 (Invitation
 *     Lifecycle empty/pending states), §15 (Shared Family Member
 *     Management overview), §20 (UX Copy Library).
 *   · UXP-001 editorial voice.
 *   · NET-001 identity (via server-side session).
 *   · COL-001 authorisation (all writes re-authorised server-side).
 *
 * Copy verbatim from the ratified PDS — do not paraphrase.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Users, Send, Clock, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ShareThisCruiseSheet from "./ShareThisCruiseSheet";

// M3-A · Copy-link is the ONLY delivery mechanism for Milestone 3.
// No external email provider is invoked. The link points at the
// hash-routed Invitation Review page (`#family-sharing/review/<id>`).
function _invitationLinkFor(invitationId) {
  try {
    return `${window.location.origin}/#family-sharing/review/${invitationId}`;
  } catch {
    return `#family-sharing/review/${invitationId}`;
  }
}
async function _copyInvitationLink(invitationId) {
  const url = _invitationLinkFor(invitationId);
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      toast.success("Invitation link copied.");
      return;
    }
  } catch { /* fall through to prompt fallback */ }
  // Clipboard API unavailable (Safari private mode, older WebViews).
  // Show the URL so the traveler can select-and-copy manually. Uses
  // window.prompt because it is universally available on all our
  // supported native surfaces (iOS WebView, Android WebView, desktop).
  try { window.prompt("Copy this invitation link:", url); } catch { /* ignore */ }
}

const FTUE_DISMISS_KEY = "cc.family_sharing.ftue.dismissed_at";
// PO directive · Milestone 1 · Dismissal Behaviour: "reappear after a
// reasonable interval." Seven days is short enough to preserve
// discoverability, long enough that the panel does not feel repetitive.
const FTUE_DISMISS_DAYS = 7;

function isRecentlyDismissed() {
  try {
    const raw = window.localStorage.getItem(FTUE_DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < FTUE_DISMISS_DAYS;
  } catch {
    // localStorage unavailable (privacy mode, older WebViews) — err on
    // the side of showing FTUE so the traveler is never silently
    // deprived of the surface.
    return false;
  }
}

function persistDismissal() {
  try {
    window.localStorage.setItem(FTUE_DISMISS_KEY, String(Date.now()));
  } catch {
    /* fail-quiet — see comment above */
  }
}

export default function FamilySharingPanel({ cruiseId, cruiseName, alwaysShow = false }) {
  const [status, setStatus] = useState(null); // {has_ever_shared: bool}
  const [statusError, setStatusError] = useState(null);
  const [summary, setSummary] = useState(null); // {roster: [], pending_invitations: []}
  const [dismissed, setDismissed] = useState(() => (alwaysShow ? false : isRecentlyDismissed()));
  const [sheetOpen, setSheetOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get("/api/family-sharing/planner-status");
      setStatus(res.data || { has_ever_shared: false });
    } catch (err) {
      setStatusError(err);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    if (!cruiseId) return;
    try {
      const res = await api.get(`/api/cruises/${cruiseId}/family-sharing/summary`);
      setSummary(res.data || { roster: [], pending_invitations: [] });
    } catch {
      // A 403 here means the caller isn't the Family Planner for this
      // cruise (Shared Family Member view arrives in later milestones);
      // a network error should not blow up Cruise Home either. Fail
      // quietly and let the panel behave like the empty state.
      setSummary({ roster: [], pending_invitations: [] });
    }
  }, [cruiseId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Panel state resolution — see PDS §13.1 empty states.
  if (!status && !statusError) return null;
  if (statusError) return null;

  const hasSummaryData = summary && (summary.roster?.length > 0 || summary.pending_invitations?.length > 0);
  const showFtue = !status.has_ever_shared && !hasSummaryData && !dismissed;

  // Edit Cruise mount (DES-FAMILY-SHARING-EXPERIENCE v1.2 · UXP-001 §17-A):
  // when `alwaysShow` is set, the panel is embedded inside the Edit Cruise
  // drawer as the canonical Family Sharing configuration surface. The
  // "return null" branch below (planner has shared before, but not this
  // cruise, and no pending invitation) collapses the surface on Cruise
  // Home. Inside Edit Cruise the section must always be discoverable —
  // so we fall through to the roster surface which renders the empty
  // state + "Invite family" CTA.
  if (!showFtue && !hasSummaryData && !alwaysShow) return null;

  const onInviteCta = () => setSheetOpen(true);
  const onSent = () => {
    // Milestone 2 · M2-D (Invitation Confidence): immediately refresh
    // the summary so the Pending row appears without a page reload.
    loadSummary();
    // A successful send also flips has_ever_shared → true; refresh
    // status so future navigations reflect the change.
    loadStatus();
  };

  return (
    <>
      {showFtue ? (
        <FamilySharingFtue
          onInvite={onInviteCta}
          onDismiss={() => { persistDismissal(); setDismissed(true); }}
        />
      ) : (
        <FamilySharingRoster
          summary={summary}
          onInvite={onInviteCta}
        />
      )}
      <ShareThisCruiseSheet
        cruiseId={cruiseId}
        cruiseName={cruiseName}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSent={onSent}
      />
    </>
  );
}

/**
 * FTUE surface. Anatomy per PDS §11.1 v1.1 verbatim.
 */
function FamilySharingFtue({ onInvite, onDismiss }) {
  return (
    <section
      data-testid="cc-family-sharing-panel"
      className="cc-card cc-fade-up cc-delay-3 p-8"
    >
      <div
        data-testid="cc-family-sharing-ftue"
        className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start"
      >
        <FriendlyIllustration />

        <div className="space-y-4">
          <div className="cc-overline text-[#1e6de5]">Family Sharing</div>

          <h3 className="font-display font-bold text-[#0F172A] text-[24px] leading-tight">
            Invite family to follow your cruise.
          </h3>

          <p className="text-[14px] text-[#475569] leading-relaxed max-w-xl">
            Everyone you invite can follow along without changing anything.
          </p>

          <ul className="space-y-2 pt-1">
            <OutcomeBullet>They'll always know what's happening next.</OutcomeBullet>
            <OutcomeBullet>Plans stay up to date automatically.</OutcomeBullet>
            <OutcomeBullet>You decide what gets shared.</OutcomeBullet>
          </ul>

          <div className="pt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={onInvite}
              data-testid="cc-family-sharing-share-cta"
              className="rounded-full px-5 py-2.5 text-[13px] font-bold text-white inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #3ea1ff 0%, #1e6de5 100%)",
                boxShadow: "0 8px 22px rgba(30,109,229,0.35)",
              }}
            >
              <Users className="w-4 h-4" strokeWidth={2.25} />
              Invite family
            </button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="cc-family-sharing-dismiss"
              className="rounded-full px-4 py-2 text-[13px] font-semibold text-[#475569] border border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] hover:text-[#0F172A] hover:border-[#CBD5E1] transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Roster + Pending Invitations surface (Milestone 2).
 *
 * Renders once the Family Planner has shared or has a pending invitation
 * on this cruise. Per PDS §7.1 the panel voice remains warm — this is
 * NOT an admin table. Each row narrates a traveler outcome, not a
 * permission.
 */
function FamilySharingRoster({ summary, onInvite }) {
  const roster = summary?.roster || [];
  const pending = summary?.pending_invitations || [];
  return (
    <section
      data-testid="cc-family-sharing-panel"
      className="cc-card cc-fade-up cc-delay-3 p-8 space-y-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cc-overline text-[#1e6de5]">Family Sharing</div>
          <h3 className="font-display font-bold text-[#0F172A] text-[20px] leading-tight mt-1">
            Who's following this cruise
          </h3>
        </div>
        <button
          type="button"
          onClick={onInvite}
          data-testid="cc-family-sharing-invite-more"
          className="rounded-full px-4 py-2 text-[12.5px] font-bold text-white inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5 flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, #3ea1ff 0%, #1e6de5 100%)",
            boxShadow: "0 8px 22px rgba(30,109,229,0.35)",
          }}
        >
          <Users className="w-4 h-4" strokeWidth={2.25} />
          Invite family
        </button>
      </div>

      {roster.length === 0 && pending.length === 0 && (
        <p data-testid="cc-family-sharing-empty" className="text-[13.5px] text-[#64748B] leading-relaxed">
          No one is sharing this cruise yet.
        </p>
      )}

      {roster.length > 0 && (
        <ul data-testid="cc-family-sharing-roster" className="space-y-2">
          {roster.map((r) => (
            <li
              key={r.share_id}
              data-testid={`cc-family-sharing-roster-row-${r.share_id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={r.shared_display_name} />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-[#0F172A] truncate">
                    {r.shared_display_name || "Family member"}
                  </div>
                  <div className="text-[11.5px] text-[#64748B]">
                    Following since {formatShortDate(r.created_at)}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
            </li>
          ))}
        </ul>
      )}

      {pending.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11.5px] font-black tracking-[0.14em] uppercase text-[#475569]">
            Pending invitations
          </div>
          <ul data-testid="cc-family-sharing-pending-list" className="space-y-2">
            {pending.map((p) => (
              <li
                key={p.invitation_id}
                data-testid={`cc-family-sharing-pending-row-${p.invitation_id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-[#DCEAFB] bg-[#F5FAFF] px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <PendingIcon />
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-[#0F172A] truncate">
                      {p.recipient_display_name || p.recipient_email || "Pending invitee"}
                    </div>
                    <div className="text-[11.5px] text-[#64748B] flex items-center gap-1.5">
                      <Send className="w-3 h-3" />
                      Invited {formatShortDate(p.created_at)}
                    </div>
                  </div>
                </div>
                {/* M3-A · Copy invitation link (only delivery mechanism). */}
                <button
                  type="button"
                  onClick={() => _copyInvitationLink(p.invitation_id)}
                  data-testid={`cc-family-sharing-copy-link-${p.invitation_id}`}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#DCEAFB] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1e6de5] hover:bg-[#EFF6FF] transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy invitation link
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Avatar({ name }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      aria-hidden="true"
      className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0"
      style={{ background: "linear-gradient(135deg, #3ea1ff 0%, #1e6de5 100%)" }}
    >
      {initial}
    </div>
  );
}

function PendingIcon() {
  return (
    <div
      aria-hidden="true"
      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: "#DCEAFB", color: "#1e6de5" }}
    >
      <Clock className="w-4 h-4" strokeWidth={2.25} />
    </div>
  );
}

function formatShortDate(iso) {
  if (!iso) return "recently";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "recently";
  }
}

/**
 * Editorial illustration slot per PDS §11.1 v1.1 — warmer editorial
 * scene: a family (three silhouettes) on a dock at golden hour, watching
 * their cruise ship on the horizon. Inline SVG to avoid external image
 * pipeline dependencies during Milestones 1–2.
 */
function FriendlyIllustration() {
  return (
    <div
      aria-hidden="true"
      className="w-[128px] h-[112px] rounded-2xl overflow-hidden relative flex-shrink-0"
      style={{
        background:
          "linear-gradient(180deg, #FFE7B8 0%, #FFC98A 42%, #E9A87B 62%, #7EB0D8 62%, #5B93C8 100%)",
        boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.08)",
      }}
    >
      <svg viewBox="0 0 128 112" width="128" height="112" fill="none" preserveAspectRatio="none">
        <circle cx="88" cy="66" r="10" fill="#FFF2CE" opacity="0.75" />
        <circle cx="88" cy="66" r="6" fill="#FFFFFF" opacity="0.55" />
        <g transform="translate(72,60)" fill="#1E3A5F" opacity="0.85">
          <path d="M0 6 L36 6 L34 10 L2 10 Z" />
          <rect x="6" y="0" width="24" height="6" />
          <rect x="10" y="-3" width="14" height="3" />
          <rect x="14" y="-6" width="6" height="3" />
          <rect x="20" y="-9" width="3" height="6" />
        </g>
        <path
          d="M60 74 Q76 76 92 74"
          stroke="#FFFFFF"
          strokeOpacity="0.55"
          strokeWidth="1.2"
          strokeLinecap="round"
          fill="none"
        />
        <rect x="0" y="98" width="128" height="14" fill="#8A5B3C" />
        <rect x="0" y="97" width="128" height="1.5" fill="#6E462B" opacity="0.6" />
        <g fill="#0F172A">
          <circle cx="34" cy="76" r="4.5" />
          <path d="M28 98 C28 88 30 82 34 82 C38 82 40 88 40 98 Z" />
          <circle cx="46" cy="79" r="4" />
          <path d="M41 98 C41 90 43 84 46 84 C49 84 51 90 51 98 Z" />
          <circle cx="56" cy="84" r="3" />
          <path d="M52 98 C52 92 54 88 56 88 C58 88 60 92 60 98 Z" />
          <path
            d="M48 90 Q52 91 55 91"
            stroke="#0F172A"
            strokeWidth="0.9"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </svg>
    </div>
  );
}

function OutcomeBullet({ children }) {
  return (
    <li className="flex items-start gap-2 text-[13.5px] text-[#334155] leading-relaxed">
      <span
        aria-hidden="true"
        className="mt-[3px] w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "#DCEAFB", color: "#1e6de5" }}
      >
        <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
          <path d="M2 6.5 L5 9 L10 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{children}</span>
    </li>
  );
}
